import { useContext, useMemo } from 'react'
import { isEmpty, values, map, pipe, unnest, groupBy } from 'ramda'
import { useTranslation } from 'react-i18next'
import cn from 'classnames/bind'

import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Loading from 'app/common/components/Loader'
import moment from 'app/common/lib/momentRange'
import { useMonthDaysAsStrings } from 'app/common/hooks/useMonthDays'
import { BannerContent } from 'app/common/components-next/banner'
import { useCurrencySign } from 'app/common/components-next/currency'
import { PlainSideSlider, useDetailsControls } from 'app/common/ui-next/plain-side-slider'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'

import { Table, HeaderCell, HeaderRow } from 'app/common/components/Table'

import RowClickable from './RowClickable'
import { Purchases } from '../PurchasesApi'
import ItemDetails from './ItemDetails'

import styles from './ScheduleTable.module.scss';

const cx = cn.bind(styles)

type Props = {
  timezone: string
  // hideSomeColumns?: boolean,
  date: string // YYYY-MM
}

const ScheduleTable = ({ timezone, date }: Props) => {
  if (!timezone) {
    console.error("ScheduleTable — не пришел обязательный параметр 'timezone'") // eslint-disable-line no-console
  }
  const { t } = useTranslation('cafeteria')
  const currency = useCurrencySign('commaSpaceBefore')
  const tableHeaders = t('schedule_table_columns', { returnObjects: true })
  const {
    content: { purchases },
    loading,
  } = useContext(Purchases)
  const days = useMonthDaysAsStrings(date)

  const scheduleObject = useMemo(
    () =>
      pipe(
        values,
        map(({ datetime, individual, items, guid, created_by: createdBy }: any) => {
          const mDate = moment(datetime).tz(timezone)
          const day = mDate.format('DD.MM')
          const year = mDate.format('YYYY')
          const time = mDate.format('HH:mm')
          return map(
            ({ title, n, total, cost, id }) => ({
              id,
              individual,
              total,
              cost,
              day,
              time,
              year,
              guid,
              createdBy,
              title,
              n,
            }),
            values(items),
          )
        }),
        unnest,
        groupBy(({ day }) => day),
      )(purchases),
    [purchases, timezone],
  )

  const emptyPeriod = isEmpty(days)
  const emptyOrders = isEmpty(scheduleObject)

  // const {
  //   detailsId, // здесь это day
  //   detailsOpen,
  //   handleCloseDetails,
  //   handleOpenDetails,
  // } = useDetailsControls()

  const { detailsID, detailsOpened, closeDetails, openDetails } = useDetailsControls()

  return (
    <>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            reference={ref}
            className={cx({ sticky })}
            shadowBottom={shadow}
            skipPadding
            flex
          >
            <Table className={styles.table}>
              <HeaderRow hideBorderBottom>
                <HeaderCell>{tableHeaders[0]}</HeaderCell>
                <HeaderCell grow={10}>
                  {tableHeaders[1]}
                  {currency}
                </HeaderCell>
              </HeaderRow>
            </Table>

            <BannerContent
              placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
              className={styles.bannerPlaceholder}
              displayAsPlaceholder
              height={0}
            />
          </PageTemplateContent>
        )}
      </Sticky>
      <PageTemplateContent className={styles.layout} skipPadding flex>
        <Table className={styles.table}>
          {emptyOrders && !loading && (
            <EmptyWarn>{t('schedule_table_empty', 'Нет покупок в выбранный период')}</EmptyWarn>
          )}

          {loading && (emptyPeriod || emptyOrders) && <Loading />}

          {!(emptyPeriod || emptyOrders) &&
            days.map((day) => (
              <RowClickable
                id={day}
                key={day}
                items={scheduleObject[day]}
                loading={loading}
                onClick={openDetails}
              />
            ))}
        </Table>

        <BannerContent
          // leaveContainerOnEmpty
          refreshOnMount
          className={styles.bannerContent}
          placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
        />
      </PageTemplateContent>

      <PlainSideSlider
        open={detailsOpened}
        onCrossClick={closeDetails}
        position={{
          top: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <ItemDetails data={(detailsID && scheduleObject[detailsID]) || []} />
      </PlainSideSlider>
    </>
  )
}

export default ScheduleTable
